import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/api";
import "../styles/StaffPages.css";

function Tile({ label, description, onClick, href, small }) {
    const [hovered, setHovered] = useState(false);
    const [copyHovered, setCopyHovered] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();
        const url = window.location.origin + href;
        navigator.clipboard.writeText(url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div style={{ display: "flex", alignItems: "stretch", gap: "8px" }}>
            <button
                onClick={onClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    padding: small ? "12px 14px" : "18px 20px",
                    border: "1px solid",
                    borderColor: hovered ? "#1a1a1a" : "#e2e2e2",
                    borderRadius: "14px",
                    background: hovered ? "#fafafa" : "#fff",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    transform: hovered ? "translateY(-2px)" : "none",
                    boxShadow: hovered ? "0 4px 20px rgba(0,0,0,0.06)" : "none",
                    fontFamily: "'Baikal Book', sans-serif",
                    textAlign: "left",
                    flex: 1,
                }}
            >
                <div>
                    <div style={{ fontSize: small ? "12px" : "14px", fontWeight: 500, color: "#1a1a1a" }}>{label}</div>
                    {description && <div style={{ fontSize: small ? "11px" : "13px", color: "#888", marginTop: "2px" }}>{description}</div>}
                </div>
            </button>
            {href && (
                <div style={{ position: "relative", display: "flex" }}>
                {copied && (
                    <div style={{
                        position: "absolute",
                        bottom: "calc(100% + 8px)",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "#1a1a1a",
                        color: "#fff",
                        fontSize: "12px",
                        fontFamily: "'Baikal Book', sans-serif",
                        padding: "6px 12px",
                        borderRadius: "8px",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                        animation: "fadeIn 0.15s ease",
                    }}>
                        Link copied!
                    </div>
                )}
                <button
                    onClick={handleCopy}
                    onMouseEnter={() => setCopyHovered(true)}
                    onMouseLeave={() => setCopyHovered(false)}
                    title="Copy link"
                    style={{
                        background: copyHovered ? "#fafafa" : "#fff",
                        border: "1px solid",
                        borderColor: copyHovered ? "#1a1a1a" : "#e2e2e2",
                        borderRadius: "14px",
                        padding: "0 14px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        color: copied ? "#2D7A50" : "#999",
                    }}
                >
                    {copied ? (
                        <svg width="22" height="22" viewBox="0 0 18 18" fill="none">
                            <path d="M4 9L7.5 12.5L14 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    ) : (
                        <svg width="22" height="22" viewBox="0 0 18 18" fill="none">
                            <rect x="6" y="6" width="9" height="9" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M12 6V4.5A1.5 1.5 0 0010.5 3H4.5A1.5 1.5 0 003 4.5v6A1.5 1.5 0 004.5 12H6" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                    )}
                </button>
                </div>
            )}
        </div>
    );
}

export default function SchoolPortalPage() {
    const { schoolId } = useParams();
    const navigate = useNavigate();
    const [school, setSchool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSchool = async () => {
            try {
                const res = await apiService.getOnboardingTimeline(schoolId);
                setSchool(res.school);
            } catch (err) {
                console.error("Failed to fetch school:", err);
                setError("School not found.");
            } finally {
                setLoading(false);
            }
        };
        fetchSchool();
    }, [schoolId]);

    if (loading) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#666", fontSize: "16px" }}>Loading...</span>
            </div>
        );
    }

    if (error || !school) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ color: "#cc3333", fontSize: "16px" }}>{error || "School not found."}</span>
            </div>
        );
    }

    return (
        <div style={{ minHeight: "100vh", background: "#ffffff", padding: "3rem 1.5rem" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "8px" }}>
                {school.image && (
                    <img
                        src={school.image}
                        alt={school.name}
                        style={{ width: 48, height: 48, borderRadius: "10px", objectFit: "contain" }}
                    />
                )}
                <h1 className="staff-page-header" style={{ marginBottom: 0 }}>{school.name}</h1>
            </div>
            <p className="staff-page-subheader">Partner portal</p>

            <div style={{ marginTop: "48px" }}>
                <h2 className="staff-section-header">Onboarding</h2>
                <p className="staff-page-content">
                    Launch phases, weekly tasks, and action items
                </p>
                <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Tile
                        label="Timeline"
                        description="View launch phases, weekly tasks, and action items"
                        onClick={() => navigate(`/onboarding/${schoolId}/timeline/`)}
                        href={`/onboarding/${schoolId}/timeline/`}
                    />
                </div>

                <div style={{ marginTop: "14px", paddingLeft: "10px", borderLeft: "1.5px solid #e2e2e2" }}>
                    <h3 style={{ fontSize: "10px", fontWeight: 600, color: "#ccc", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "6px" }}>Items</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Tile
                            small
                            label="Fan Access Selection"
                            description="Select the fan access experiences for your program"
                            onClick={() => navigate(`/reward-selection/${schoolId}/`)}
                            href={`/reward-selection/${schoolId}/`}
                        />
                    </div>
                </div>
            </div>

            <div style={{ marginTop: "48px" }}>
                <h2 className="staff-section-header">Resources</h2>
                <p className="staff-page-content">
                    Partner tools and information
                </p>
                <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <Tile
                        label="Merchant Partners"
                        description="Distribute to local merchant partners"
                        onClick={() => navigate(`/merchants/${schoolId}/`)}
                        href={`/merchants/${schoolId}/`}
                    />
                    <Tile
                        label="Early Access Overview"
                        description="Program walkthrough and fan experience"
                        onClick={() => navigate(`/early-access-overview/${schoolId}/`)}
                        href={`/early-access-overview/${schoolId}/`}
                    />
                </div>

                <div style={{ marginTop: "14px", paddingLeft: "10px", borderLeft: "1.5px solid #e2e2e2" }}>
                    <h3 style={{ fontSize: "10px", fontWeight: 600, color: "#ccc", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "6px" }}>Tools</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <Tile
                            small
                            label="Fan Access Revenue Model"
                            description="Interactive revenue calculator for fan access marketplace"
                            onClick={() => navigate(`/resources/fan-access/${schoolId}/`)}
                            href={`/resources/fan-access/${schoolId}/`}
                        />
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}
